import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"
import { SpaceBGMId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { DocumentStatus } from "../common"
import { zNonEmptyTextInput } from "../lib/zods"

/**
 * ISO 3166-1 alpha-2形式の国コード
 */
export const CountryCode = z.string().regex(/^[A-Z]{2}$/)

export type CountryCode = z.infer<typeof CountryCode>

export const ManageCreateSpaceBGMRequest = z.object({
  name: z.string(),
  priority: z.coerce.number().int(),
  defaultVolume: z.coerce.number().int().min(1).max(100),
  playableCountries: z.array(CountryCode).nullable().optional().describe("再生可能な国のリスト。nullまたは空配列の場合は制限なし。"),
})

export type ManageCreateSpaceBGMRequest = z.infer<typeof ManageCreateSpaceBGMRequest>

export const ManageUpdateSpaceBGMRequest = z.object({
  name: zNonEmptyTextInput.optional(),
  priority: z.coerce.number().int().optional(),
  defaultVolume: z.coerce.number().int().min(1).max(100).optional(),
  updateBGM: z.boolean().optional(),
  status: DocumentStatus.optional(),
  playableCountries: z.array(CountryCode).nullable().optional().describe("再生可能な国のリスト。nullまたは空配列の場合は制限なし。"),
})

export type ManageUpdateSpaceBGMRequest = z.infer<typeof ManageUpdateSpaceBGMRequest>

export const SpaceBGMDocumentCommon = z.object({
  name: z.string(),
  default_volume: z.number().min(0).max(100),
  trial_range: z.object({
    start_at: z.number(),
    end_at: z.number(),
  }).optional(),
  url: z.url(),
  can_delete: z.boolean(),
  status: DocumentStatus,
  playable_countries: z.array(CountryCode).nullable().optional().describe("再生可能な国のリスト。nullまたは空配列の場合は制限なし。"),
})

export const SpaceBGMDocumentMeta = z.intersection(
  SpaceBGMDocumentCommon,
  z.object({
    priority: z.int().min(0),
  })
)

export type SpaceBGMDocumentMeta = z.infer<typeof SpaceBGMDocumentMeta>

export const SpaceBGMDocumentBody = z.intersection(
  SpaceBGMDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type SpaceBGMDocumentBody = z.infer<typeof SpaceBGMDocumentBody>

export type SpaceBGMDocument = SpaceBGMDocumentBody & {
  id: SpaceBGMId
}

export class SpaceBGMDocuments {
  static toDocumentData(doc: SpaceBGMDocumentBody): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SpaceBGMDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SpaceBGMId(doc.id),
      name: data["name"],
      url: data["url"],
      default_volume: data["default_volume"],
      trial_range: data["trial_range"],
      priority: data["priority"],
      can_delete: data["can_delete"],
      status: data["status"],
      playable_countries: data["playable_countries"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
